import { useState } from 'react';
import { useAdminAuth } from '@/context/AdminAuthContext';

type SectionStatus = 'idle' | 'success' | 'error';

export default function AdminSettingsTab() {
  const { changePassword, changeUsername, currentUsername } = useAdminAuth();

  // --- Username state ---
  const [newUsername, setNewUsername] = useState('');
  const [userConfirmPassword, setUserConfirmPassword] = useState('');
  const [showUserPw, setShowUserPw] = useState(false);
  const [userStatus, setUserStatus] = useState<SectionStatus>('idle');
  const [userError, setUserError] = useState('');

  // --- Password state ---
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwStatus, setPwStatus] = useState<SectionStatus>('idle');
  const [pwError, setPwError] = useState('');

  const handleUsernameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUserStatus('idle');
    setUserError('');
    if (!newUsername.trim() || !userConfirmPassword) {
      setUserStatus('error');
      setUserError('Please fill in all fields.');
      return;
    }
    if (newUsername.trim().length < 3) {
      setUserStatus('error');
      setUserError('Username must be at least 3 characters.');
      return;
    }
    const ok = changeUsername(userConfirmPassword, newUsername.trim());
    if (ok) {
      setUserStatus('success');
      setNewUsername('');
      setUserConfirmPassword('');
    } else {
      setUserStatus('error');
      setUserError('Password is incorrect. Please try again.');
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPwStatus('idle');
    setPwError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwStatus('error');
      setPwError('Please fill in all fields.');
      return;
    }
    if (newPassword.length < 4) {
      setPwStatus('error');
      setPwError('New password must be at least 4 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwStatus('error');
      setPwError('New passwords do not match.');
      return;
    }
    const ok = changePassword(currentPassword, newPassword);
    if (ok) {
      setPwStatus('success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPwStatus('error');
      setPwError('Current password is incorrect.');
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-gray-500 text-sm mt-1">Manage your admin account credentials.</p>
      </div>

      <div className="flex flex-col gap-6 max-w-lg">

        {/* Change Username Card */}
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50">
              <i className="ri-user-settings-line text-teal-600 text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base">Change Username</h3>
              <p className="text-gray-400 text-xs">Current username: <span className="font-semibold text-teal-600">{currentUsername}</span></p>
            </div>
          </div>

          <form onSubmit={handleUsernameSubmit} className="flex flex-col gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">New Username</label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Enter new username"
                className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm with Password</label>
              <div className="relative">
                <input
                  type={showUserPw ? 'text' : 'password'}
                  value={userConfirmPassword}
                  onChange={(e) => setUserConfirmPassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowUserPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showUserPw ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            {userStatus === 'error' && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
                <span className="text-red-600 text-sm">{userError}</span>
              </div>
            )}
            {userStatus === 'success' && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg px-4 py-3">
                <i className="ri-checkbox-circle-line text-green-500 text-sm"></i>
                <span className="text-green-700 text-sm font-semibold">Username updated successfully!</span>
              </div>
            )}

            <button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-lg cursor-pointer whitespace-nowrap transition-colors text-sm"
            >
              Update Username
            </button>
          </form>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50">
              <i className="ri-lock-password-line text-teal-600 text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-base">Change Password</h3>
              <p className="text-gray-400 text-xs">Update your admin login password</p>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Current Password</label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showCurrent ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">New Password</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showNew ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            {pwStatus === 'error' && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
                <span className="text-red-600 text-sm">{pwError}</span>
              </div>
            )}
            {pwStatus === 'success' && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-100 rounded-lg px-4 py-3">
                <i className="ri-checkbox-circle-line text-green-500 text-sm"></i>
                <span className="text-green-700 text-sm font-semibold">Password updated successfully!</span>
              </div>
            )}

            <button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-lg cursor-pointer whitespace-nowrap transition-colors text-sm"
            >
              Update Password
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
